import convertToHtml from '@rei/rich-text-renderer';
import bannerGradientConfig from './bannerBackgroundConfig';
import { formatLandingBadge } from './formatCmsData';
import { formatHTMLCodes } from './formatText';

/**
 * Map image to get the correct object to display image
 * @param {Object} images that contains the keys damUrl and
 * @param {Object} options object to config the image options
 * @returns an object image with the breakpoints
 */

const formatImageComponent = (images, options) => {
  if (!images) return undefined;
  const { xs } = images || {};
  const sm = images.sm || xs;
  const md = images.md || sm;
  const lg = images.lg || md;

  return {
    images: {
      xs,
      sm,
      md,
      lg,
    },
    alt: xs?.alt || sm?.alt || md?.alt || lg?.alt || '',
    useResizing: xs?.imageType !== 'svg' && xs?.imageType !== 'gif',
    ...options,
  };
};

/**
 *
 * @param {String} ambientVideo url of the video
 * @returns video mapped, object with mp4 and webm
 */
const formatAmbientVideo = (ambientVideo) => ({
  mp4: ambientVideo?.match(/\.(mp4)$/) ? ambientVideo : null,
  webm: ambientVideo?.match(/\.(webm)$/) ? ambientVideo : null,
});

const formatHeadingConfig = (heading, headingOptions, imageAlt) => {
  const useLockupAsHeading = !heading;
  return {
    text: heading || imageAlt,
    srOnly: useLockupAsHeading,
    ...headingOptions,
  };
};

/**
 * @param {*} actions Array of ctas that will use the content block component
 * @param {*} actionOptions Object to enable and set a unique analyticsId
 * @returns Returns an object with javascript and defaultAction
 */
const formatActionsConfig = (actions, actionOptions) => {
  let analyticsId = '';
  let ariaLabel = '';
  const {
    allSecondaryButtons,
    heading,
    horizontalSection,
    subheading,
    useCustomAnalyticId,
    useCustomText,
  } = actionOptions || {};

  const actionsMap = {
    defaultActions: [],
    javascriptActions: [],
    alternateLink: {},
    allSecondaryButtons,
  };

  actions?.forEach((cta) => {
    if (useCustomAnalyticId) {
      analyticsId = `${heading || subheading || ''}_${horizontalSection}_${
        cta?.title
      }`;
    }

    if (useCustomText) {
      const formatedHeading = formatHTMLCodes(heading || subheading || '');
      ariaLabel = `${cta?.title} about ${formatedHeading}`;
    }

    if (cta?.href === 'javascript') {
      actionsMap.javascriptActions.push({
        text: cta?.title || cta?.text,
        newTab: false,
        href: '',
        target: 'javascript',
        tag: 'button',
        modifier: null,
        analyticsId,
        ariaLabel,
      });
    } else {
      actionsMap.defaultActions.push({
        text: cta?.title || cta?.text,
        target: cta?.href,
        newTab: false,
        modifier: null,
        noFollow: false,
        analyticsId,
        ariaLabel,
      });
    }
  });

  return actionsMap;
};
/**
 * This method transform the information promotion model from content stack
 * and format it to our content block structures
 * @param {Object} promotion object that contains the info to use in the content block
 * @param {Object} config object config to apply to the content block
 * @param {Object} headingOptions object config to apply to the heading of the content block
 * @returns return an object with the property to use the content block component
 */
const transformPromotionToContentBlock = (
  promotion = {},
  config = {},
  headingOptions = {},
  actionsOptions = {}
) => {
  const {
    lockup = {},
    copy: {
      heading, eyebrow, subheading, description: body,
    } = {},
    links = [],
    actions,
  } = promotion || {};

  if (
    lockup?.xs
    || eyebrow
    || heading
    || subheading
    || body
    || links?.length
    || actions
  ) {
    const formattedActions = actions || formatActionsConfig(links, {
      ...actionsOptions,
      heading,
      subheading,
    });

    const finalActions = {
      ...formattedActions,
      alternateLink: {
        href: promotion?.alternateLink?.href || actions?.alternateLink?.href,
        text: promotion?.alternateLink?.title || promotion?.alternateLink?.text
          || actions?.alternateLink?.text,
      },
    };

    return {
      heading: formatHeadingConfig(heading, headingOptions, lockup?.xs?.alt),
      body,
      subheading,
      eyebrow,
      lockup: formatImageComponent(lockup, { useResizing: false }),
      actions: finalActions,
      config,
    };
  }

  return null;
};

/**
 * Method to transform link collection model from content stack to simple top categories component
 * @param {Object} linkCollection object that contains a links properties, this property has to be
 * a linkCollection model
 * @param {String} type string to set what structure it will return, set 'default',
 * 'related-categories' or 'brands'
 * @returns return by default the structure for simple top categories component but if the type
 * is equal to brands it will return the structure for brands rail component
 */
const transformLinkCollectionToCategories = (
  linkCollection = {},
  type = 'default'
) => {
  const { links = [] } = linkCollection || {};
  if (!links?.length) return [];

  switch (type) {
    case 'brands':
      // return an object for brand rail component
      return links.map((link, i) => {
        const src = `//satchel.rei.com/media/img/brand-logos/${link?.title
          .split(' ')
          .join('-')
          .replace(/[^a-zA-Z0-9 -]/g, '')
          .toLowerCase()}.svg`;
        return {
          key: `brand-${i + 1}`,
          link: {
            title: `Shop ${link?.title}`,
            href: link?.href,
          },
          media: {
            src,
            alt: `Brand logo of ${link?.title}`,
            objectFit: 'contain',
          },
        };
      });

    case 'off-price-brands':
      // return an object for brand rail component
      return links.map((link, i) => {
        const src = `//satchel.rei.com/media/img/brand-logos/${link?.title
          .split(' ')
          .join('-')
          .replace(/[^a-zA-Z0-9 -]/g, '')
          .toLowerCase()}.svg`;
        return {
          key: `brand-${i + 1}`,
          link: {
            title: `${link?.title}`,
            href: link?.href,
          },
          media: {
            src,
            alt: `Brand logo of ${link?.title}`,
            objectFit: 'contain',
          },
        };
      });

    case 'related-categories':
      // return an object by default for related categories
      return links.map((cat, i) => ({
        text: cat?.link?.title,
        target: cat?.link?.href,
        key: `${cat?.link?.title}-${i}`,
        src: cat?.contentImage?.renditions[0]?.link,
        srcAlt: cat?.image?.altText,
        style: null,
      }));

    case 'lifestyle':
      return links.map((link, i) => {
        const image = link?.damImage?.renditions?.original;
        return {
          key: `${link?.link?.title}-${i}`,
          link: {
            title: link?.link?.title,
            href: link?.link?.href,
          },
          media: {
            src: `${image?.src}?im=Resize,width=300`,
            alt: link?.damImage?.alt || link?.alt || link?.link?.title,
            objectFit: 'cover',
          },
        };
      });

    default:
      // return an object by default for simple top category component
      return links.map((cat, i) => ({
        key: `${cat?.link?.title}-${i}`,
        link: {
          title: cat?.link?.title,
          href: cat?.link?.href,
        },
        media: {
          ...cat.image,
          objectFit: 'contain',
        },
      }));
  }
};

export const convertAnchorToXY = (anchor) => {
  if (!anchor) return null;
  const s = anchor.split(' ');
  return `${s[1]} ${s[0]}`;
};

/**
 * Destructuring the content object to return effect, height, theme or secondaryButtonsOnly
 * @param {Object} contentObj content object model
 * @returns return the value from the property desired
 */
const configDestructuring = (contentObj, property) => {
  if (!contentObj) return null;
  const configData = (contentObj.config || contentObj.visualConfiguration || {});
  if (Object.keys(configData).length === 0 && !property) return null;

  const palette = typeof configData?.palette === 'string' ? configData?.palette?.toLowerCase().split(' with ') : { ...configData?.palette };
  const anchor = (!!configData?.anchor && convertAnchorToXY(configData.anchor.toLowerCase())) || 'center center';
  const storyRowCount = configData?.storyRowCount?.toLowerCase();
  const storyStackPosition = configData?.storyStackPosition?.toLowerCase().split(' ');

  const configMap = {
    ...configData,
    theme: configData?.theme?.toLowerCase().replace(' ', '-') || 'primary',
    height: configData?.height?.toLowerCase() || 'mid',
    anchor,
    anchorMobile:
      (!!configData?.anchorMobile
        && convertAnchorToXY(configData.anchorMobile.toLowerCase()))
      || anchor,
    copyAlignment: configData?.copyAlignment?.toLowerCase(),
    copyAlignmentDesktop: configData?.copyAlignmentDesktop?.toLowerCase(),
    storyRowCount,
    storyStackPosition: storyStackPosition?.[0],
  };

  if (typeof configData?.palette === 'string') {
    configMap.palette = {
      content: palette?.[0]?.replace(' ', '-') || 'primary',
      surface:
        palette?.[1]?.replace(' ', '-').replace('-background', '') || 'primary',
    };
  }

  return configMap[property];
};

// Apply values from CMS to all breakpoints
const formatCvsValues = (val) => {
  if (!val) return null;
  return {
    xs: val,
    'xs-plus': val,
    sm: val,
    md: val,
    lg: val,
  };
};

// Apply fallbacks to defaults config that contains breakpoints
const formatCvsPerBreakpoint = (val) => {
  if (!val) return null;
  return {
    xs: val.xs,
    'xs-plus': val['xs-plus'] || val.xs,
    sm: val.sm || val['xs-plus'] || val.xs,
    md: val.md || val.sm || val['xs-plus'] || val.xs,
    lg: val.lg || val.md || val.sm || val['xs-plus'] || val.xs,
  };
};

/**
 * Format gradient of banner component
 * @param {Object} config object with the possible config of banner component
 * @param {Object} defaults object with the possible default config of banner component
 * @returns bannerHeight and bannerGradient data format to be use on banner component
 */
const formatBannerContentStack = (config = {}, defaults = {}) => {
  const { effect, imageAnchor } = config || {};
  const {
    effect: defaultEffect,
    bannerPosition,
    contentPosition,
  } = defaults || {};
  let gradientEffect;
  if (effect) {
    gradientEffect = effect.includes('Light') || effect.includes('Dark') ? effect : `Dark ${effect}`;
  }
  const [mode, ...restArray] = gradientEffect ? gradientEffect.split(' ') : ['', ''];
  const direction = restArray?.join(' ') || null;
  const bannerGradient = bannerGradientConfig[direction];

  return {
    contentPosition: formatCvsPerBreakpoint(contentPosition),
    bannerPosition:
        formatCvsValues(imageAnchor) || formatCvsPerBreakpoint(bannerPosition),
    bannerHeight: configDestructuring({ config }, 'height'),
    bannerGradient:
        formatCvsValues(bannerGradient) || formatCvsPerBreakpoint(defaultEffect),
    bannerGradientMode: mode || null,
  };
};

/**
 * transformLinkCollectionToStickyNav
 * @description Formats props for StickyNav component
 * @param contentObj {Object} content model object nested under componentModels object.
 * @exports {Object} Props
 */
const transformLinkCollectionToStickyNav = (contentObj, optStickyNav) => {
  const { links } = contentObj || {};
  const { categoryName } = optStickyNav || {};
  return (
    (!!links?.length
      && links.map((link) => {
        let ariaLabel;
        if (categoryName) {
          ariaLabel = `${link?.title} ${
            link?.title.toLowerCase() === 'learn' ? 'about ' : ''
          }${categoryName}`;
        }
        return {
          anchor: link?.href,
          text: link?.title,
          ariaLabel: ariaLabel ?? link?.title,
        };
      }))
    || []
  );
};

/**
 * formatBadgeConfigContentStack
 * @description Formats props for @rei/content-block.
 * @param contentObj {Object} content model object nested under componentModels object.
 * @param config {Object} config options for component.
 * @param headingOptions {Object} config options for heading.
 * @exports {Object} Props
 */
const formatBadgeConfigContentStack = (contentObj, palette, accentColor) => {
  const { badge } = contentObj || {};
  return {
    text: badge,
    theme: palette ?? 'light',
    accentColor: accentColor || '',
  };
};

/**
 * transformPromotionToMiniBanner
 * @description Formats props for MiniBanner component
 * @param promotion
 * @param defaults
 * @param contentBlockHeadingOpts
 * @exports {Object} Props
 */
const formatMiniBannerComponent = (
  promotion,
  defaults = {},
  contentBlockHeadingOpts = {},
  actionOptions = {}
) => {
  if (promotion && !Object.keys(promotion)?.length) return null;
  const media = formatImageComponent(promotion.image, {});
  const { padding, palette = {} } = defaults;
  const contentBlockOpts = {
    contentAlign: {
      xs: 'center',
    },
    palette,
  };
  const content = transformPromotionToContentBlock(
    promotion,
    contentBlockOpts,
    contentBlockHeadingOpts,
    actionOptions
  );

  return (
    ((media || content || padding) && {
      padding,
      media,
      content,
      palette,
    }) || null
  );
};

const transformPromotionToStory = (
  promotion,
  contentBlockOptions,
  headingOptions,
  actionsOptions,
  media,
  mediaOptions,
  palette,
) => ({
  ...promotion,
  media: formatImageComponent(media, mediaOptions),
  content: transformPromotionToContentBlock(
    promotion,
    contentBlockOptions,
    headingOptions,
    actionsOptions
  ),
  palette,
});

const transformPromotionToLandingStories = (
  promotions,
  landingStoryOptions,
  headingSectionText,
  headingSectionOptions,
  contentBlockOptions,
  contentBlockHeadingOptions,
  mediaOptions
) => {
  const {
    aspectRatio, grid, storyStackPosition, palette, objectFit,
  } = landingStoryOptions || {};

  const stories = promotions?.map((promotion, index) => transformPromotionToStory(
    promotion,
    contentBlockOptions,
    contentBlockHeadingOptions,
    {
      ...promotion.actionsOptions,
      horizontalSection: index + 1,
    },
    promotion.image,
    mediaOptions,
    palette,
  )) || null;

  return {
    stories,
    headingSection: {
      ...headingSectionOptions,
      text: headingSectionText,
    },
    aspectRatio,
    grid,
    storyStackPosition,
    palette,
    objectFit,
  };
};

const transformLinksToAdditionalSavings = (contentObj) => {
  const { heading, links } = contentObj || {};
  return (!!links?.length && {
    heading,
    links: links.map((link) => ({
      target: link?.href,
      text: link?.title,
    })),
  }) || null;
};

const formatFrequentlyAsked = (content) => {
  const { heading, questionsAndAnswers } = content || {};
  return (
    (!!questionsAndAnswers?.length && {
      title: heading,
      questionsAndAnswers:
        (!!questionsAndAnswers?.length
          && questionsAndAnswers.map((faq) => ({
            question: faq?.question,
            answer: convertToHtml(faq?.answer),
          })))
        || null,
    })
    || null
  );
};

const transformLandingFeaturedTilesComponent = (contentObj, config) => {
  const { categories = {} } = contentObj || {};
  const featuredTiles = Object.values(categories).map((category) => ({
    action: {
      text: category?.link?.title,
      href: category?.link?.href,
    },
    media: {
      alt: category?.image?.alt,
      xs: category?.image?.src,
      sm: category?.image?.src,
      md: category?.image?.src,
      lg: category?.image?.src,
    },
    body: convertToHtml(category?.body),
  }));
  return (
    (!!featuredTiles.length && {
      featuredTiles,
      config,
    }) || null
  );
};

export const transformImageAnchorsToBannerPosition = ({
  xs: xsAnchor,
  sm: smAnchor,
  md: mdAnchor,
  lg: lgAnchor,
} = {}) => {
  const xs = xsAnchor ? convertAnchorToXY(xsAnchor.toLowerCase()) : 'center center';
  const sm = smAnchor ? convertAnchorToXY(smAnchor.toLowerCase()) : xs;
  const md = mdAnchor ? convertAnchorToXY(mdAnchor.toLowerCase()) : sm;

  return {
    xs,
    sm,
    md,
    lg: lgAnchor ? convertAnchorToXY(lgAnchor.toLowerCase()) : md,
  };
};

/**
 * This method is to format the OfferSet data into format to be
 * consumed in Offer Cards
 *
 * @param offerSet - offer set cms data
 * @return {*|null}
 */
const formatOfferCards = (offerSet) => (offerSet && offerSet.map((offer) => ({
  ...offer,
  badge: formatLandingBadge({
    config: {
      'badge-text': offer.badge,
      'badge-theme': offer.theme,
    },
  }),
  media: formatImageComponent(offer.image, {
    renderedSizes: {
      mobile: '100vw',
      desktop: '548px',
    },
  }),
}))) || null;

const transformContentBlockSize = (size) => (size?.toLowerCase()?.replace(' ', '-') || 'md-standard');

const transformLeadRailItem = ({ content, visualConfiguration }) => {
  const { effect } = visualConfiguration || {};
  let gradientEffect;
  if (effect) {
    gradientEffect = effect.includes('Light') || effect.includes('Dark') ? effect : `Dark ${effect}`;
  }
  const [mode, ...restArray] = gradientEffect ? gradientEffect.split(' ') : ['', ''];
  const direction = restArray?.join(' ') || null;
  const bannerGradient = bannerGradientConfig[direction];

  return {
    content: {
      ...content,
      media: formatImageComponent(content.media, {
        renderedSizes: {
          mobile: '611px',
          desktop: '543px',
        },
      }),
      badge: formatLandingBadge({
        config: {
          'badge-text': content.badge,
        },
      }),
      lockup: formatImageComponent(content.lockup, { useResizing: false }),
    },
    visualConfiguration: {
      ...visualConfiguration,
      bannerGradient:
        formatCvsValues(bannerGradient) || formatCvsPerBreakpoint(effect),
      bannerGradientMode: mode || null,
    },
  };
};

const slidesVisibleValues = {
  'Lifestyle 3:4 Large': 3,
  'Lifestyle 1:1': 3,
  'Lifestyle 3:4 Small': 4,
};

const formatContentToFilmstrip = (content, slides, visualConfiguration) => ({
  content,
  slideStyle: visualConfiguration?.cardStyle,
  slidesVisible: slidesVisibleValues[visualConfiguration?.cardStyle] ?? 4,
  slides: slides?.map((slide) => ({
    ...slide?.content,
    cta: {
      ...slide?.content?.actions?.defaultActions[0],
      target: slide?.content?.actions?.defaultActions[0]?.href,
    },
    media: formatImageComponent(slide?.content?.media, {
      renderedSizes: {
        mobile: '33vw',
        desktop: '25vw',
      },
    }),
  })),
});

const formatContentToStories = (stories) => ({
  stories: stories?.contentGroup?.map((story, index) => ({
    ...story,
    dataLocationIndex: index + 1,
    content: {
      ...story.content,
      config: {
        ...story?.visualConfiguration,
      },
    },
    media: formatImageComponent(story.content.media),
  })),
  headingSection: stories?.content.heading,
  rowCount: stories?.visualConfiguration?.rowCount,
  stackPosition: stories?.visualConfiguration?.stackPosition,
});

export {
  formatImageComponent,
  formatAmbientVideo,
  transformPromotionToContentBlock,
  transformLinkCollectionToCategories,
  formatBannerContentStack,
  configDestructuring,
  transformLinkCollectionToStickyNav,
  formatBadgeConfigContentStack,
  formatMiniBannerComponent,
  transformPromotionToStory,
  transformPromotionToLandingStories,
  transformLinksToAdditionalSavings,
  formatFrequentlyAsked,
  transformLandingFeaturedTilesComponent,
  formatOfferCards,
  transformContentBlockSize,
  transformLeadRailItem,
  formatContentToFilmstrip,
  formatContentToStories,
};
